import { createAsyncThunk } from '@reduxjs/toolkit';
import { setMemberData } from '../reducers/member';
import processMember from '../utils/processors/processMember';
import { fetchHistoricalOrders } from './fetchHistoricalOrders';
import { fetchOffers } from './fetchOffers';
import { getEnableDynamicPaymentGatewayConfig } from '../selectors/config';
import { fetchTransactionHistory } from './fetchTransactionHistory';
import { fetchPaymentGatewayConfig } from './fetchPaymentGatewayConfig';
import { fetchPaymentGatewayTokens } from './fetchPaymentGatewayTokens';
import { fetchFavouriteOrders } from './fetchFavouriteOrders';
import { fetchLocationOffers } from './fetchLocationOffers';

export const setMember = createAsyncThunk(
  '$setMember',
  async (
    data: {
      member?: Member;
      rawMember?: RawMember;
      supressFetchingAssociatedData?: boolean;
    },
    { dispatch, rejectWithValue, getState },
  ) => {
    const { member, rawMember, supressFetchingAssociatedData } = data;

    try {
      if (!member && !rawMember) {
        throw new Error('no member data supplied');
      }

      let processedMember = member;

      if (rawMember) {
        processedMember = processMember(rawMember);
      }

      if (processedMember === undefined) {
        throw new Error('processed member is undefined somehow');
      }

      dispatch(setMemberData(processedMember));

      dispatch(fetchLocationOffers());

      dispatch(fetchOffers({}));

      let authenticationMethod: 'trusted' | 'member' | 'none';

      if (member || rawMember) {
        authenticationMethod = 'member';
      } else {
        authenticationMethod = 'none';
      }

      await dispatch(
        fetchFavouriteOrders({
          authenticationMethod,
        }),
      );

      // We should only fetch historical orders with the limit of 10. This gets run everytime the user logs in or
      // when the user has placed an order - in this scenario it acts as a prefetch for the app to show the recent
      // orders in the home screen. When the user goes to the historical orders screen, it fetches all of the orders
      // without the limit. In the future though we should have chunking loading for the historical orders
      dispatch(
        fetchHistoricalOrders({
          limit: 10,
        }),
      );

      dispatch(fetchTransactionHistory({}));

      if (!supressFetchingAssociatedData) {
        const enableDynamicPaymentGatewayConfig =
          getEnableDynamicPaymentGatewayConfig(
            getState() as EntireFrontendState,
          );

        if (enableDynamicPaymentGatewayConfig) {
          dispatch(fetchPaymentGatewayConfig({}));
        }
      }

      dispatch(fetchPaymentGatewayTokens({}));

      return processedMember;
    } catch (e) {
      console.error('Set member failed', e);
      return rejectWithValue(e);
    }
  },
);
